import React, { useState } from 'react';
import { X, MapPin, Plus, Trash2, AlertCircle, Loader, Printer } from 'lucide-react';
import { ScanTextInput, NumericInput } from '../../../common/inputs';
import { useTranslation } from '../../../../contexts/I18nProvider';
import {
  directReceiptService,
  DirectPlacementItem
} from '../../../../services/directReceiptService';
import type { LineWithArrived } from './search/POrderLineBrowser';
import { LabelPrintModal } from '../../../common/LabelPrintModal';

interface DirectPlacementModalProps {
  orderNo: number;
  lines: LineWithArrived[];
  onClose: () => void;
  onSuccess: (placedCount: number) => void;
}

/** En (lokasjon, antall)-rad for en linje. Flere rader = linja splittes over flere lokasjoner. */
interface PlacementRow {
  location: string; // tom = bruk felles lokasjon
  quantity: string;
}

const remainingFor = (line: LineWithArrived): number => {
  const ordered = line.quantity || 0;
  const handled = (line.receivedQuantity || 0) + (line.arrivedQuantity || 0);
  return Math.max(0, ordered - handled);
};

const fmtQty = (n: number) => (Math.round((n || 0) * 1000) / 1000).toString();

/**
 * Sammensatt nøkkel (orderNo-lineNo). LineNo alene er IKKE unik på tvers av ordrer i et
 * forsendelsesmottak (flere innkjøpsordrer kan hver ha «Linje 1»). Uten denne kolliderer
 * per-linje-state, og to linjer deler samme antall/lokasjon (jf. cross-order-bug).
 * For enkelt-ordre-mottak er nøkkelen funksjonelt identisk med lineNo → ingen atferdsendring.
 */
const lineKey = (line: LineWithArrived): string => `${line.orderNo}-${line.lineNo}`;

/**
 * Norlux P19/P20 — "Legg direkte ut på lokasjon".
 * Plasserer valgte ordrelinjer direkte på lokasjon uten ankomstregistrering. Hver linje kan splittes
 * over flere lokasjoner via «+ Legg til lokasjon» (P20) — uavhengig av hvor mange linjer som er valgt.
 * Felles lokasjon (skann) brukes for rader uten egen lokasjon. Ett atomisk kall til backend.
 */
export const DirectPlacementModal: React.FC<DirectPlacementModalProps> = ({
  orderNo,
  lines,
  onClose,
  onSuccess
}) => {
  const { t } = useTranslation();

  const [commonLocation, setCommonLocation] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Linja det skrives ut vareetikett for (åpner den delte LabelPrintModal). Null = lukket.
  const [printLine, setPrintLine] = useState<LineWithArrived | null>(null);

  // Per linje (nøkkel = orderNo-lineNo): én eller flere (lokasjon, antall)-rader. Default én rad med gjenstående antall.
  const [rowsByLine, setRowsByLine] = useState<Record<string, PlacementRow[]>>(() => {
    const init: Record<string, PlacementRow[]> = {};
    lines.forEach((l) => {
      init[lineKey(l)] = [{ location: '', quantity: fmtQty(remainingFor(l)) }];
    });
    return init;
  });

  const updateRow = (key: string, idx: number, patch: Partial<PlacementRow>) =>
    setRowsByLine((prev) => ({
      ...prev,
      [key]: prev[key].map((r, i) => (i === idx ? { ...r, ...patch } : r))
    }));

  const addRow = (key: string) =>
    setRowsByLine((prev) => ({ ...prev, [key]: [...prev[key], { location: '', quantity: '' }] }));

  const removeRow = (key: string, idx: number) =>
    setRowsByLine((prev) => ({
      ...prev,
      [key]: prev[key].length > 1 ? prev[key].filter((_, i) => i !== idx) : prev[key]
    }));

  const lineSum = (key: string) =>
    (rowsByLine[key] || []).reduce((s, r) => s + (parseFloat(r.quantity) || 0), 0);

  // Auto-restlinje: når et delvis antall registreres på en linje (sum < gjenstående) legges det
  // automatisk opp en ny rad med restantallet, så operatøren kan plassere resten på en annen lokasjon.
  // Kjøres på blur (ikke pr. tastetrykk). Legger kun til når noe er fylt inn, det faktisk mangler antall,
  // og ingen rad allerede står med tomt antall (unngår stabling av tomme rader).
  const ensureRestRow = (line: LineWithArrived) => {
    const key = lineKey(line);
    setRowsByLine((prev) => {
      const rows = prev[key] || [];
      const remaining = remainingFor(line);
      const sum = rows.reduce((s, r) => s + (parseFloat(r.quantity) || 0), 0);
      const rest = remaining - sum;
      const hasEmptyQty = rows.some((r) => !(parseFloat(r.quantity) > 0));
      if (sum > 0.001 && rest > 0.001 && !hasEmptyQty) {
        return { ...prev, [key]: [...rows, { location: '', quantity: fmtQty(rest) }] };
      }
      return prev;
    });
  };

  // Bygger placements gruppert per innkjøpsordre. Backend-endepunktet er per ordre, så et
  // forsendelsesmottak som spenner over flere ordrer gir én gruppe per ordre. Enkelt-ordre
  // (normaltilfellet) gir nøyaktig én gruppe → ett kall, uendret oppførsel.
  const buildPlacementsByOrder = (): { byOrder: Map<number, DirectPlacementItem[]>; error?: string } => {
    const byOrder = new Map<number, DirectPlacementItem[]>();
    for (const line of lines) {
      for (const row of rowsByLine[lineKey(line)] || []) {
        const loc = (row.location || '').trim() || commonLocation.trim();
        const qty = parseFloat(row.quantity);
        if (!loc) {
          return { byOrder: new Map(), error: t('warehouse.modules.receipt.directPlacement.errorMissingLocation', {}, 'Velg lokasjon for alle rader (eller en felles lokasjon)') };
        }
        if (!qty || qty <= 0) {
          return { byOrder: new Map(), error: t('warehouse.modules.receipt.directPlacement.errorInvalidQuantity', {}, 'Antall må være større enn 0 på alle rader') };
        }
        const item: DirectPlacementItem = {
          lineNo: line.lineNo,
          itemNo: line.itemNo,
          locationCode: loc,
          quantity: qty,
          warehouse: line.warehouse,
          itemBatchNo: line.itemBatchNo
        };
        const list = byOrder.get(line.orderNo) || [];
        list.push(item);
        byOrder.set(line.orderNo, list);
      }
    }
    if (byOrder.size === 0) {
      return { byOrder: new Map(), error: t('warehouse.modules.receipt.directPlacement.errorNoPlacements', {}, 'Ingen rader å legge ut') };
    }
    return { byOrder };
  };

  const handleSubmit = async () => {
    setError(null);
    const { byOrder, error: buildError } = buildPlacementsByOrder();
    if (buildError) {
      setError(buildError);
      return;
    }

    // Advarsel ved under-mottak: total utlagt < gjenstående på én eller flere linjer.
    // Beregnes per linje via den sammensatte nøkkelen (ikke via lineNo, som kolliderer på tvers av ordrer).
    const shortfalls = lines
      .map((l) => {
        const placed = (rowsByLine[lineKey(l)] || []).reduce((s, r) => s + (parseFloat(r.quantity) || 0), 0);
        return { line: l, placed, remaining: remainingFor(l) };
      })
      .filter((x) => x.remaining - x.placed > 0.001);

    if (shortfalls.length > 0) {
      const detail = shortfalls
        .map((s) => {
          const desc = (s.line.description1 || '').trim();
          return `${s.line.itemNo}${desc ? ' ' + desc : ''}: ${fmtQty(s.placed)} / ${fmtQty(s.remaining)}`;
        })
        .join('\n');
      const warn = t(
        'warehouse.modules.receipt.directPlacement.underReceiptWarning',
        { '0': shortfalls.length.toString(), '1': detail },
        `Du legger ut totalt færre enn gjenstående på ${shortfalls.length} linje(r). Resten blir ikke mottatt nå:\n\n${detail}\n\nVil du fortsette?`
      );
      if (!window.confirm(warn)) return;
    }

    setSubmitting(true);
    try {
      // Backend-endepunktet er atomisk PER ordre. Et forsendelsesmottak kan spenne over flere
      // ordrer, så vi kaller én gang per ordre og aggregerer. Enkelt-ordre = nøyaktig ett kall.
      const orders = Array.from(byOrder.entries());
      let totalPlaced = 0;
      const succeeded: number[] = [];
      const failed: { orderNo: number; message: string }[] = [];

      for (const [ordNo, placements] of orders) {
        try {
          const res = await directReceiptService.processDirectPlacement(ordNo, {
            placements,
            notes: notes.trim() || undefined
          });
          if (res.success) {
            totalPlaced += res.totalPlaced;
            succeeded.push(ordNo);
          } else {
            const firstFail = res.results?.find((r) => !r.success);
            failed.push({ orderNo: ordNo, message: res.errorMessage || firstFail?.errorMessage || t('warehouse.modules.receipt.directPlacement.errorGeneric', {}, 'Kunne ikke legge ut varene') });
          }
        } catch (e) {
          failed.push({ orderNo: ordNo, message: e instanceof Error ? e.message : t('warehouse.modules.receipt.directPlacement.errorGeneric', {}, 'Kunne ikke legge ut varene') });
        }
      }

      if (failed.length === 0) {
        onSuccess(totalPlaced);
        return;
      }

      // Delvis suksess: fullførte ordrer er allerede persistert og kan ikke rulles tilbake.
      // Fjern dem fra state slik at et nytt forsøk IKKE dobbel-plasserer dem; kun feilede ordrer
      // blir igjen i skjemaet. (For enkelt-ordre skjer dette aldri — da er det alt-eller-ingenting.)
      if (succeeded.length > 0) {
        setRowsByLine((prev) => {
          const next: Record<string, PlacementRow[]> = {};
          for (const l of lines) {
            if (!succeeded.includes(l.orderNo)) {
              const k = lineKey(l);
              if (prev[k]) next[k] = prev[k];
            }
          }
          return next;
        });
      }

      const failDetail = failed.map((f) => `${f.orderNo}: ${f.message}`).join('\n');
      setError(
        succeeded.length > 0
          ? t(
              'warehouse.modules.receipt.directPlacement.partialOrderFailure',
              { '0': succeeded.join(', '), '1': failDetail },
              `Lagt ut for ordre ${succeeded.join(', ')}. Feilet for:\n${failDetail}\n\nDe utlagte ordrene er lagret — prøv på nytt for de som feilet.`
            )
          : failDetail
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 sm:p-4 z-[60]">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
        <div className="p-4 sm:p-6">
          {/* Header */}
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-2">
              <MapPin className="h-5 w-5 sm:h-6 sm:w-6 text-amber-600 flex-shrink-0" />
              <div>
                <h2 className="text-lg sm:text-xl font-bold text-gray-900">
                  {t('warehouse.modules.receipt.directPlacement.title', {}, 'Legg direkte ut på lokasjon')}
                </h2>
                <p className="text-gray-600 text-xs sm:text-sm mt-0.5">
                  {t('warehouse.modules.receipt.orderHeader', { orderNo }, `Ordre ${orderNo}`)}
                  {' · '}
                  {t('warehouse.modules.receipt.directPlacement.noArrivalNote', {}, 'Uten ankomstregistrering')}
                </p>
              </div>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 ml-2 flex-shrink-0">
              <X className="h-5 w-5 sm:h-6 sm:w-6" />
            </button>
          </div>

          {/* Felles lokasjon */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('warehouse.modules.receipt.directPlacement.commonLocation', {}, 'Felles lokasjon')}
            </label>
            <ScanTextInput
              value={commonLocation}
              onChange={(e) => setCommonLocation(e.target.value)}
              placeholder={t('warehouse.modules.receipt.directPlacement.scanLocation', {}, 'Skann eller skriv lokasjon...')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
            />
            <p className="text-xs text-gray-500 mt-1">
              {t('warehouse.modules.receipt.directPlacement.commonLocationHint', {}, 'Brukes for alle rader uten egen lokasjon nedenfor.')}
            </p>
          </div>

          {/* Per linje: én eller flere (lokasjon, antall)-rader */}
          <div className="space-y-3">
            {lines.filter((line) => rowsByLine[lineKey(line)]).map((line) => {
              const key = lineKey(line);
              const rows = rowsByLine[key] || [];
              const remaining = remainingFor(line);
              const sum = lineSum(key);
              return (
                <div key={key} className="border border-gray-200 rounded-lg p-3">
                  {/* To rader, ikke én (TW 36335793).
                      Varenummeret var det ENESTE elementet paa raden uten beskyttelse —
                      beskrivelsen har truncate, "Gjenstaaende" har whitespace-nowrap og
                      knappen har flex-shrink-0 — saa flexbox krympet varenummeret foerst.
                      Bindestreken i f.eks. 200521000-S ga da et pent linjeskift midt i koden.
                      Varenummeret er operatoerens viktigste identifikator og maa staa helt.
                      Rad 1 har kun to elementer og kan derfor ikke klemme det. */}
                  <div className="mb-2">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-gray-900 text-sm whitespace-nowrap flex-shrink-0">{line.itemNo}</span>
                      <button
                        type="button"
                        onClick={() => setPrintLine(line)}
                        className="ml-auto p-1.5 rounded hover:bg-amber-50 text-amber-600 flex-shrink-0 min-h-[36px] min-w-[36px] flex items-center justify-center"
                        title={t('warehouse.modules.receipt.directPlacement.printLabel', {}, 'Skriv ut vareetikett')}
                        aria-label={t('warehouse.modules.receipt.directPlacement.printLabel', {}, 'Skriv ut vareetikett')}
                      >
                        <Printer size={16} />
                      </button>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500 truncate">{line.description1 || ''}</span>
                      <span className="ml-auto text-xs text-gray-500 whitespace-nowrap flex-shrink-0">
                        {t('warehouse.modules.receipt.directPlacement.remaining', {}, 'Gjenstående')}: {fmtQty(remaining)} {line.itemUnitTxt}
                      </span>
                    </div>
                  </div>

                  {rows.map((row, idx) => (
                    <div key={idx} className="flex items-end gap-2 mb-2">
                      <div className="flex-1">
                        {idx === 0 && (
                          <label className="block text-xs text-gray-500 mb-0.5">
                            {t('warehouse.modules.receipt.directPlacement.location', {}, 'Lokasjon')}
                          </label>
                        )}
                        <ScanTextInput
                          value={row.location}
                          onChange={(e) => updateRow(key, idx, { location: e.target.value })}
                          placeholder={t('warehouse.modules.receipt.directPlacement.useCommon', {}, 'Bruk felles lokasjon')}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                        />
                      </div>
                      <div className="w-24 sm:w-28">
                        {idx === 0 && (
                          <label className="block text-xs text-gray-500 mb-0.5">
                            {t('warehouse.modules.receipt.directPlacement.quantity', {}, 'Antall')}
                          </label>
                        )}
                        <NumericInput
                          mode="decimal"
                          quantity
                          value={row.quantity}
                          onChange={(e) => updateRow(key, idx, { quantity: e.target.value })}
                          onBlur={() => ensureRestRow(line)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                        />
                      </div>
                      <button
                        onClick={() => removeRow(key, idx)}
                        disabled={rows.length <= 1}
                        className="p-2 text-gray-400 hover:text-red-600 disabled:opacity-30 disabled:cursor-not-allowed"
                        aria-label={t('common.buttons.delete', {}, 'Slett')}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}

                  <div className="flex items-center justify-between">
                    <button
                      onClick={() => addRow(key)}
                      className="inline-flex items-center gap-1 text-sm font-medium text-amber-700 hover:text-amber-800 py-1"
                    >
                      <Plus className="h-4 w-4" /> {t('warehouse.modules.receipt.directPlacement.addRow', {}, 'Legg til lokasjon')}
                    </button>
                    {rows.length > 1 && (
                      <span className="text-xs text-gray-500">
                        {t('warehouse.modules.receipt.directPlacement.sum', {}, 'Sum')}: <span className="font-medium">{fmtQty(sum)}</span> / {fmtQty(remaining)} {line.itemUnitTxt}
                        {Math.abs(sum - remaining) > 0.001 && (
                          <span className="ml-1 text-amber-700">{t('warehouse.modules.receipt.directPlacement.sumMismatch', {}, '(avviker fra gjenstående)')}</span>
                        )}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Notat */}
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('warehouse.modules.receipt.directPlacement.notes', {}, 'Notat (valgfritt)')}
            </label>
            <input
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
            />
          </div>

          {/* Feilmelding */}
          {error && (
            <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
              <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700 break-words">{error}</p>
            </div>
          )}

          {/* Footer */}
          <div className="mt-6 flex items-center justify-end gap-2">
            <button
              onClick={onClose}
              disabled={submitting}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              {t('common.buttons.cancel', {}, 'Avbryt')}
            </button>
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="inline-flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg text-sm font-medium hover:bg-amber-700 disabled:opacity-50"
            >
              {submitting ? <Loader className="h-4 w-4 animate-spin" /> : <MapPin className="h-4 w-4" />}
              {t('warehouse.modules.receipt.directPlacement.confirm', {}, 'Legg ut')}
            </button>
          </div>
        </div>
      </div>
    </div>

    {printLine && (
      // z-[70]-wrapper sikrer at LabelPrintModal (egen z-50) legger seg OPPÅ denne modalen (z-[60]).
      <div className="relative z-[70]">
        <LabelPrintModal
          isOpen={true}
          onClose={() => setPrintLine(null)}
          printType="item"
          items={[{
            id: printLine.itemNo,
            displayName: printLine.description1 ? `${printLine.itemNo} - ${printLine.description1}` : printLine.itemNo,
            itemNo: printLine.itemNo,
            itemDescription: printLine.description1
          }]}
        />
      </div>
    )}
    </>
  );
};

export default DirectPlacementModal;
